import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { EmptyState, ErrorState, Skeleton } from '../ui/index.js'

/**
 * CONVENTIONS §3: every async surface has loading, empty and error states. One helper so the
 * six lanes render the same three states with the same Turkish copy and the same retry.
 */

export type ScreenStatus = 'loading' | 'empty' | 'error' | 'ready'

export type ScreenStateProps = {
  status: ScreenStatus
  onRetry: () => void
  /** Turkish, from content/copy. Falls back to the shared empty line. */
  emptyText?: string
  errorText?: string
  errorDetail?: string | null
  children: ReactNode
}

export function ScreenState({
  status,
  onRetry,
  emptyText,
  errorText,
  errorDetail,
  children,
}: ScreenStateProps) {
  const { t } = useTranslation()

  if (status === 'loading') {
    return (
      <div className="state" aria-busy="true" aria-live="polite">
        <span className="visually-hidden">{t('app.loading')}</span>
        <Skeleton height={140} radius="var(--radius-m)" />
        <Skeleton height={20} width="70%" />
        <Skeleton height={20} width="45%" />
      </div>
    )
  }
  if (status === 'empty') return <EmptyState text={emptyText ?? t('empty.generic')} />
  if (status === 'error') {
    return (
      <ErrorState
        text={errorText ?? t('error.generic')}
        retryLabel={t('app.retry')}
        onRetry={onRetry}
        detail={errorDetail}
      />
    )
  }
  return <>{children}</>
}

/**
 * The screenshot gate needs each state on demand, so in dev `?state=loading|empty|error`
 * forces one. It is compiled out of the production bundle.
 */
export function useForcedStatus(actual: ScreenStatus): ScreenStatus {
  const [params] = useSearchParams()
  if (!import.meta.env.DEV) return actual
  const forced = params.get('state')
  if (forced === 'loading' || forced === 'empty' || forced === 'error') return forced
  return actual
}
